import _, { get } from "lodash";
import { Close, Icon } from "@shared/ui";
import React, { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "../hooks/useNavigate";
import Form from "../components/Form";

import { getUserInfo } from "../lib/auth";
import { getTeam, inviteTeamMember } from "../lib/automatic";
import { AdminContext, ClientContext } from "../App";
import { CLIENT_ID_PARAM } from "../lib/constants";
const InviteTeamMember = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { promiseInProgress } = usePromiseTracker();

  // Setup state
  const [error, setError] = useState(null);
  const [newMember, setNewMember] = useState({});
  const [team, setTeam] = useState([]);
  const [userId, setUserId] = useState({});
  const userRole = useContext(AdminContext);
  const [availableRoles, setAvailableRoles] = useState(["member", "admin"]);
  const { selectedClient, platformClientId } = useContext(ClientContext);

  useEffect(() => {
    trackPromise(
      getTeam(selectedClient).then((data) => {
        setTeam(data);
      })
    );

    getUserInfo().then((data) => {
      const currentUser = get(data, "username");
      setUserId(currentUser);

      // Set available roles based on user's role and selected client
      const isPlatformAdmin = userRole === "platform-admin";
      const isCurrentClientPlatformClient = selectedClient === platformClientId;

      // Determine available roles based on conditions
      if (isPlatformAdmin) {
        if (isCurrentClientPlatformClient) {
          // Platform admin can create any role when on platform client
          setAvailableRoles([
            "member",
            "admin",
            "platform-member",
            "platform-admin",
            "platform-analyst",
          ]);
        } else {
          // Platform admin can only create member or admin when on destination client
          setAvailableRoles(["member", "admin"]);
        }
      } else {
        // Regular admin can only create member or admin
        setAvailableRoles(["member", "admin"]);
      }
    });
  }, [selectedClient, platformClientId, userRole]);

  // Preserve client query param in navigation
  const navigateWithClientParam = (path) => {
    const clientId = searchParams.get(CLIENT_ID_PARAM) || selectedClient;
    if (clientId) {
      navigate(`${path}?${CLIENT_ID_PARAM}=${clientId}`);
    } else {
      navigate(path);
    }
  };

  // Go back with client query param preserved
  const handleGoBack = () => {
    navigate(-1);
  };

  // Setup New Ignition Session form fields
  const inviteTeamMemberFields = [
    {
      id: "firstName",
      label: "First Name",
      type: "text",
      placeholder: "Team member first name...",
    },
    {
      id: "lastName",
      label: "Last Name",
      type: "text",
      placeholder: "Team member last name...",
    },
    {
      id: "email",
      label: "Email",
      type: "email",
      placeholder: "Team member email address...",
    },
    {
      id: "role",
      label: "Role",
      type: "dropdown",
      values: availableRoles,
      placeholder: "Team member role...",
    },
  ];

  // Create new member and send ignitionSession link on form submit; return to ignitionSessions
  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      // Invite team member

      const isEmailTaken = _.some(team, { email: get(newMember, "email") });

      if (isEmailTaken) {
        throw new Error("Email already taken");
      }

      const member = await inviteTeamMember(newMember, selectedClient);

      // Inform dashboard member of success and navigate to new created session
      toast.success("Invite sent");
      navigateWithClientParam("/team");
    } catch (error) {
      toast.error(error.message);
      setError(error.message);
      console.error(error);
    }
  };

  return (
    <div className="w-full h-full bg-white z-10 flex flex-col gap-y-2 absolute">
      <div className="p-8 flex items-center justify-between border-b border-gray-200">
        <h1 className="text-2xl font-bold">Invite Team Member </h1>
        <div className="flex gap-x-4 items-center">
          <Icon
            icon={Close}
            height="h-4"
            width="w-4"
            className="cursor-pointer transition hover:scale-110"
            onClick={handleGoBack}
          />
        </div>
      </div>
      <div className="p-8 flex flex-col gap-y-8">
        {promiseInProgress === true ? <p>Loading...</p> : null}
        <Form
          name="invite-team-member"
          fields={inviteTeamMemberFields}
          formData={newMember}
          setFormData={setNewMember}
          onSubmit={onSubmit}
          error={error}
          button="Invite Team Member"
        />
      </div>
    </div>
  );
};

export default InviteTeamMember;
